import React, { useEffect, useRef, useState } from 'react';
import { sendCode, verifyCode, passwordLogin } from './api';
import { isEmailish, isValidIdentifier } from '../utils/identify';

const cfg = (typeof window !== 'undefined' && window.ACCOUNT_AUTH) || {};

// Email, a GR number (10 digits) or a Cypriot one (8) — shared with the checkout
// gate in utils/identify.js, which mirrors cn_norm_identifier() in PHP.
const isEmail = isEmailish;
const validId = isValidIdentifier;

// Fire an analytics event (mirrors the checkout identify tracking) so we can compare
// account-page auth with the in-checkout one. Never throws.
function track(action, extra) {
  try {
    if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture('account_auth', { action, ...extra });
    }
  } catch (e) { /* noop */ }
}

// Passwordless-first login/registration for the My Account page. Enter email or
// phone → one-time code → signed in (new identifiers get a minimal account, no
// name). A subtle "use a password instead" link reveals the classic password login
// for existing email accounts.
export default function AccountAuth() {
  const [phase, setPhase] = useState('id'); // id | code | password
  const [identifier, setIdentifier] = useState('');
  const [channel, setChannel] = useState('');
  const [isNew, setIsNew] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef(null);

  useEffect(() => { track('shown'); }, []);
  useEffect(() => { if (phase === 'code' && codeRef.current) codeRef.current.focus(); }, [phase]);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const done = (data) => { window.location.assign((data && data.redirect) || cfg.redirect || '/my-account/'); };

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    if (busy || !validId(identifier)) { setErr('Βάλε έγκυρο email ή κινητό τηλέφωνο.'); return; }
    setBusy(true); setErr(''); setNote('');
    const { ok, data } = await sendCode(identifier.trim());
    setBusy(false);
    if (!ok) {
      if (data.password && data.loginUrl) { window.location.assign(data.loginUrl); return; } // admins → password login
      setErr(data.message || 'Κάτι πήγε στραβά.'); if (data.cooldown) setCooldown(data.cooldown); return;
    }
    track('code_sent', { channel: data.channel, is_new: !!data.isNew });
    setChannel(data.channel); setIsNew(!!data.isNew); setCooldown(data.cooldown || 45);
    setNote(data.channel === 'phone' ? 'Σου στείλαμε κωδικό με SMS.' : 'Σου στείλαμε κωδικό στο email σου.');
    setPhase('code');
  };

  const submitCode = async (e) => {
    if (e) e.preventDefault();
    if (busy || (code.match(/\d/g) || []).length !== 6) { setErr('Βάλε τον 6ψήφιο κωδικό.'); return; }
    setBusy(true); setErr('');
    const { ok, data } = await verifyCode(identifier.trim(), code);
    setBusy(false);
    if (!ok) { setErr(data.message || 'Λάθος κωδικός.'); return; }
    track(isNew ? 'registered' : 'logged_in', { method: 'otp', channel });
    done(data);
  };

  const submitPassword = async (e) => {
    if (e) e.preventDefault();
    if (busy || !password) { setErr('Βάλε τον κωδικό πρόσβασης.'); return; }
    setBusy(true); setErr('');
    const { ok, data } = await passwordLogin(identifier.trim(), password);
    setBusy(false);
    if (!ok) { setErr(data.message || 'Λάθος κωδικός πρόσβασης.'); return; }
    track('logged_in', { method: 'password', channel: isEmail(identifier) ? 'email' : 'phone' });
    done(data);
  };

  return (
    <div className="cn-acct">
      <h2 className="cn-acct__title">Σύνδεση ή εγγραφή</h2>

      {phase === 'id' && (
        <form className="cn-acct__form" onSubmit={requestCode} noValidate>
          <label className="cn-acct__label" htmlFor="cn-acct-id">Email ή κινητό τηλέφωνο</label>
          <input
            id="cn-acct-id" type="text" className="cn-acct__input" autoComplete="username"
            placeholder="π.χ. name@email.com, 69XXXXXXXX ή 9XXXXXXX" value={identifier}
            onChange={(e) => { setIdentifier(e.target.value); setErr(''); }} autoFocus
          />
          <p className="cn-acct__hint">Θα σου στείλουμε έναν κωδικό μιας χρήσης — χωρίς κωδικό πρόσβασης.</p>
          {err && <p className="cn-acct__err" role="alert">{err}</p>}
          <button type="submit" className="cn-acct__btn" disabled={busy}>
            {busy ? 'Αποστολή…' : 'Συνέχεια'}
          </button>
          {isEmail(identifier) && (
            <button type="button" className="cn-acct__altlink" onClick={() => { setErr(''); setPhase('password'); }}>
              Σύνδεση με κωδικό πρόσβασης
            </button>
          )}
        </form>
      )}

      {phase === 'code' && (
        <form className="cn-acct__form" onSubmit={submitCode} noValidate>
          <button type="button" className="cn-acct__back" onClick={() => { setPhase('id'); setCode(''); setErr(''); }}>← Αλλαγή</button>
          {note && <p className="cn-acct__note">{note} <strong>{identifier.trim()}</strong>{isNew ? ' — θα δημιουργήσουμε τον λογαριασμό σου.' : ''}</p>}
          <label className="cn-acct__label" htmlFor="cn-acct-code">Κωδικός 6 ψηφίων</label>
          <input
            id="cn-acct-code" ref={codeRef} type="text" inputMode="numeric" maxLength={6}
            className="cn-acct__input cn-acct__input--code" placeholder="______"
            value={code} onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setErr(''); }}
          />
          {err && <p className="cn-acct__err" role="alert">{err}</p>}
          <button type="submit" className="cn-acct__btn" disabled={busy}>{busy ? 'Έλεγχος…' : 'Σύνδεση'}</button>
          <button type="button" className="cn-acct__altlink" disabled={cooldown > 0 || busy} onClick={() => requestCode()}>
            {cooldown > 0 ? `Νέος κωδικός σε ${cooldown}″` : 'Στείλε νέο κωδικό'}
          </button>
        </form>
      )}

      {phase === 'password' && (
        <form className="cn-acct__form" onSubmit={submitPassword} noValidate>
          <button type="button" className="cn-acct__back" onClick={() => { setPhase('id'); setPassword(''); setErr(''); }}>← Πίσω</button>
          <label className="cn-acct__label" htmlFor="cn-acct-id2">Email</label>
          <input id="cn-acct-id2" type="text" className="cn-acct__input" autoComplete="username" value={identifier}
            onChange={(e) => { setIdentifier(e.target.value); setErr(''); }} />
          <label className="cn-acct__label" htmlFor="cn-acct-pw">Κωδικός πρόσβασης</label>
          <input id="cn-acct-pw" type="password" className="cn-acct__input" autoComplete="current-password"
            value={password} onChange={(e) => { setPassword(e.target.value); setErr(''); }} />
          {err && <p className="cn-acct__err" role="alert">{err}</p>}
          <button type="submit" className="cn-acct__btn" disabled={busy}>{busy ? 'Σύνδεση…' : 'Σύνδεση'}</button>
          <button type="button" className="cn-acct__altlink" onClick={() => { setErr(''); setPassword(''); setPhase('id'); }}>
            Σύνδεση με κωδικό μιας χρήσης
          </button>
          {cfg.lostPasswordUrl && <a className="cn-acct__altlink" href={cfg.lostPasswordUrl}>Ξέχασες τον κωδικό;</a>}
        </form>
      )}
    </div>
  );
}
